// Shared SVG builders for legend-style icons. Used by the legend panel and
// (behind a feature flag) the canvas context-menu "Create from model…"
// submenu so model entries show their shape + color instead of a generic
// circle / diagonal-line icon.

import {strokeDashArray, strokeLineCap} from '@maporium-workspace/shared';
import {cyNodeShape} from './node-shape.helper';

const DEFAULT_NODE_COLOR = '#333';
const DEFAULT_LINK_COLOR = '#333';
const DEFAULT_STROKE_WIDTH = 2;

function escapeAttr(value: string): string {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function starPath(cx: number, cy: number, outer: number, inner: number, attrs: string): string {
  const points: string[] = [];
  const step = Math.PI / 5;
  for (let i = 0; i < 10; i++) {
    const r = i % 2 === 0 ? outer : inner;
    const a = -Math.PI / 2 + i * step;
    points.push(`${(cx + r * Math.cos(a)).toFixed(2)},${(cy + r * Math.sin(a)).toFixed(2)}`);
  }
  return `<polygon points="${points.join(' ')}" ${attrs}/>`;
}

export interface ShapeRenderOptions {
  /** Canvas size of the output SVG (px). Default 18 for the legend, 12 for menu icons. */
  size?: number;
  /** Stroke width override (otherwise read from element or defaults to 2). */
  strokeWidth?: number;
}

export function buildNodeShapeSvg(element: any, opts: ShapeRenderOptions = {}): string {
  const color = element?.color || DEFAULT_NODE_COLOR;
  const strokeColor = element?.strokeColor || color;
  const rawStroke = opts.strokeWidth ?? (element?.strokeWidth ? Number(element.strokeWidth) : DEFAULT_STROKE_WIDTH);
  const strokeWidth = Math.max(1, Math.min(3, rawStroke));
  const type = cyNodeShape(element?.type);
  const borderStyle = String(
    element?.properties?.borderStyle ?? element?.borderStyle ?? 'solid'
  ).toLowerCase();
  const imageUrl = element?.imageUrl || element?.image;

  const size = opts.size ?? 18;

  // MAP-1459: Models with thickness 0 and an image render as a borderless
  // image filling the bounding box — no shape outline.
  const weight = element?.weight;
  if (imageUrl && (weight === 0 || weight === '0')) {
    return `<svg viewBox="0 0 ${size} ${size}" width="${size}" height="${size}" xmlns="http://www.w3.org/2000/svg">` +
      `<image href="${escapeAttr(imageUrl)}" x="0" y="0" width="${size}" height="${size}" preserveAspectRatio="xMidYMid meet"/>` +
      `</svg>`;
  }

  const pad = strokeWidth;
  const inner = size - pad * 2;
  const cx = size / 2;
  const cy = size / 2;

  const dashArray = strokeDashArray(borderStyle, strokeWidth);
  const dashAttr = dashArray ? ` stroke-dasharray="${dashArray}"` : '';
  const capAttr = dashArray ? ` stroke-linecap="${strokeLineCap(borderStyle)}"` : '';
  const attrs = `fill="none" stroke="${strokeColor}" stroke-width="${strokeWidth}" stroke-linejoin="round"${dashAttr}${capAttr}`;

  let shape: string;
  switch (type) {
    case 'rectangle':
    case 'square':
      shape = `<rect x="${pad}" y="${pad}" width="${inner}" height="${inner}" rx="1" ${attrs}/>`;
      break;
    case 'round-rectangle':
    case 'roundrectangle':
      shape = `<rect x="${pad * 0.5}" y="${pad + 1}" width="${size - pad}" height="${inner - 2}" rx="3" ${attrs}/>`;
      break;
    case 'triangle':
      shape = `<polygon points="${cx},${pad} ${size - pad},${size - pad} ${pad},${size - pad}" ${attrs}/>`;
      break;
    case 'diamond':
    case 'rhomboid':
      shape = `<polygon points="${cx},${pad} ${size - pad},${cy} ${cx},${size - pad} ${pad},${cy}" ${attrs}/>`;
      break;
    case 'pentagon':
      shape = `<polygon points="${cx},${pad} ${size - pad},${cy - 1} ${size - pad - 2},${size - pad} ${pad + 2},${size - pad} ${pad},${cy - 1}" ${attrs}/>`;
      break;
    case 'hexagon':
      shape = `<polygon points="${pad + 3},${pad} ${size - pad - 3},${pad} ${size - pad},${cy} ${size - pad - 3},${size - pad} ${pad + 3},${size - pad} ${pad},${cy}" ${attrs}/>`;
      break;
    case 'octagon':
      shape = `<polygon points="${pad + 3},${pad} ${size - pad - 3},${pad} ${size - pad},${pad + 3} ${size - pad},${size - pad - 3} ${size - pad - 3},${size - pad} ${pad + 3},${size - pad} ${pad},${size - pad - 3} ${pad},${pad + 3}" ${attrs}/>`;
      break;
    case 'star':
      shape = starPath(cx, cy, inner / 2, inner / 4, attrs);
      break;
    case 'ellipse':
    default:
      shape = `<ellipse cx="${cx}" cy="${cy}" rx="${(size - pad) / 2 - 0.5}" ry="${(size - pad) / 2 - 0.5}" ${attrs}/>`;
      break;
  }

  const imageOverlay = imageUrl
    ? `<image href="${escapeAttr(imageUrl)}" x="${pad + 1}" y="${pad + 1}" width="${inner - 2}" height="${inner - 2}" preserveAspectRatio="xMidYMid meet"/>`
    : '';

  return `<svg viewBox="0 0 ${size} ${size}" width="${size}" height="${size}" xmlns="http://www.w3.org/2000/svg">${shape}${imageOverlay}</svg>`;
}

export function buildLinkShapeSvg(element: any, opts: ShapeRenderOptions = {}): string {
  const color = element?.color || DEFAULT_LINK_COLOR;
  const rawStroke = opts.strokeWidth ?? (element?.strokeWidth ? Number(element.strokeWidth) : DEFAULT_STROKE_WIDTH);
  const strokeWidth = Math.max(1, Math.min(3, rawStroke));
  const lineStyle = String(element?.shape || 'solid').toLowerCase();
  const directed = element?.direction !== 'undirected';
  const targetArrow = element?.targetArrowShape || element?.arrowTarget || (directed ? 'triangle' : 'none');
  const sourceArrow = element?.sourceArrowShape || element?.arrowSource ||
    (element?.direction === 'bidirected' ? targetArrow : undefined);

  const width = opts.size ?? 22;
  const height = Math.max(10, Math.round(width * 0.55));
  const cy = height / 2;

  const dashPattern = strokeDashArray(lineStyle, strokeWidth);
  const dashArray = dashPattern ? `stroke-dasharray="${dashPattern}"` : '';

  const xStart = sourceArrow && sourceArrow !== 'none' ? 5 : 1;
  const xEnd = targetArrow && targetArrow !== 'none' ? width - 5 : width - 1;

  // Dashed stays butt; solid keeps round for a cleaner join into arrowheads.
  const lineCap = lineStyle === 'solid' ? 'round' : strokeLineCap(lineStyle);
  const line = `<line x1="${xStart}" y1="${cy}" x2="${xEnd}" y2="${cy}" stroke="${color}" stroke-width="${strokeWidth}" ${dashArray} stroke-linecap="${lineCap}"/>`;

  const head = (arrow: string | undefined, atEnd: boolean): string => {
    if (!arrow || arrow === 'none') return '';
    const x = atEnd ? width - 1 : 1;
    const dir = atEnd ? -1 : 1;
    switch (String(arrow).toLowerCase()) {
      case 'triangle':
      case 'triangle-backcurve':
        return `<polygon points="${x},${cy} ${x + dir * 5},${cy - 3} ${x + dir * 5},${cy + 3}" fill="${color}"/>`;
      case 'triangle-tee':
        return `<polygon points="${x},${cy} ${x + dir * 5},${cy - 3} ${x + dir * 5},${cy + 3}" fill="${color}"/>` +
          `<line x1="${x + dir * 5}" y1="${cy - 3}" x2="${x + dir * 5}" y2="${cy + 3}" stroke="${color}" stroke-width="${strokeWidth}"/>`;
      case 'circle':
        return `<circle cx="${x + dir * 3}" cy="${cy}" r="2.5" fill="none" stroke="${color}" stroke-width="${strokeWidth}"/>`;
      case 'diamond':
        return `<polygon points="${x},${cy} ${x + dir * 3},${cy - 2.5} ${x + dir * 6},${cy} ${x + dir * 3},${cy + 2.5}" fill="${color}"/>`;
      case 'square':
        return `<rect x="${atEnd ? x - 5 : x}" y="${cy - 2.5}" width="5" height="5" fill="${color}"/>`;
      case 'tee':
        return `<line x1="${x + dir * 1}" y1="${cy - 3}" x2="${x + dir * 1}" y2="${cy + 3}" stroke="${color}" stroke-width="${strokeWidth}"/>`;
      case 'vee':
        return `<polyline points="${x + dir * 5},${cy - 3} ${x},${cy} ${x + dir * 5},${cy + 3}" fill="none" stroke="${color}" stroke-width="${strokeWidth}"/>`;
      default:
        return `<polygon points="${x},${cy} ${x + dir * 5},${cy - 3} ${x + dir * 5},${cy + 3}" fill="${color}"/>`;
    }
  };

  const sourceHead = sourceArrow ? head(sourceArrow, false) : '';
  const targetHead = head(targetArrow, true);

  return `<svg viewBox="0 0 ${width} ${height}" width="${width}" height="${height}" xmlns="http://www.w3.org/2000/svg">${line}${sourceHead}${targetHead}</svg>`;
}

/**
 * Encode an SVG string as a data URI for use in `<img src>`, CSS `url()`, or
 * cytoscape-contextmenu `image.src`. Uses base64 to sidestep edge-case
 * URL-encoding issues (hash fragments in color strings, etc.).
 */
export function svgToDataUri(svg: string): string {
  // btoa only handles latin-1; SVG here is ASCII-safe.
  return `data:image/svg+xml;base64,${btoa(svg)}`;
}

// Image-bearing models pass the image URL straight through; an <image href>
// inside a base64 data:image/svg+xml is loaded in a sandboxed image context
// that blocks external resource fetches, yielding the broken-image icon.
// At 12px the shape outline is barely visible behind the image anyway.
export function buildNodeShapeSrcForContextMenu(element: any, opts: ShapeRenderOptions = {}): string {
  const imageUrl = element?.imageUrl || element?.image;
  if (imageUrl) {
    return imageUrl;
  }
  return svgToDataUri(buildNodeShapeSvg(element, opts));
}

